import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xingternal/button';

import ProgressIndicator from './ProgressIndicator';
import { useStepForm, StepFormContextProvider } from './StepFormContext';
import { ProgressIndicatorCopyType } from './types';
import styles from './styles.css';

type StepFormFooterProps = {
  onSubmit?: () => void;
  backButtonText?: string;
  submitButtontext: string;
  to?: string;
  canGoBack?: boolean;
  disabled?: boolean;
  loading?: boolean;
  customLeftButton?: JSX.Element | null;
};

type StepFormProps = {
  children: React.ReactNode;
  stepDescriptions: Array<ProgressIndicatorCopyType>;
  initialStep: number;
  onChangeStepCallback: (step: number) => void;
};

const StepFormFooter = ({
  onSubmit = () => {},
  backButtonText = 'BUTTON_BACK"',
  submitButtontext = 'EP_PAGE_CREATION_SAVE_NEXT',
  to,
  canGoBack = true,
  disabled = false,
  loading = false,
  customLeftButton = null,
}: StepFormFooterProps) => {
  const { step, setStep } = useStepForm();

  const backClick = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <>
      <div className={styles.footerWizard}>
        <div className={styles.customLeftButton}>{customLeftButton}</div>
        <div className={styles.actionButtons}>
          {canGoBack && (
            <Button size={'medium'} onClick={backClick} disabled={disabled}>
              <FormattedMessage id={backButtonText} />
            </Button>
          )}
          {to ? (
            <Button
              variant={'primary'}
              size={'medium'}
              onClick={onSubmit}
              loading={loading}
              to={to}
              data-testid="SUBMIT_BUTTON"
            >
              <FormattedMessage id={submitButtontext} />
            </Button>
          ) : (
            <Button
              variant={'primary'}
              size={'medium'}
              onClick={onSubmit}
              loading={loading}
              disabled={disabled}
              data-testid="SUBMIT_BUTTON"
            >
              <FormattedMessage id={submitButtontext} />
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

const StepForm = ({
  children,
  stepDescriptions,
  initialStep,
  onChangeStepCallback,
}: StepFormProps) => {
  const [step, setStep] = useState(initialStep);

  const [progressIndicatorVisible, setProgressIndicatorVisible] = useState(
    true
  );

  const setStepAndCallback = (step: number) => {
    onChangeStepCallback && onChangeStepCallback(step);
    setStep(step);
  };

  return (
    <StepFormContextProvider
      value={{
        step,
        setStep: setStepAndCallback,
        setProgressIndicatorVisible,
        initialStep,
      }}
    >
      {progressIndicatorVisible && (
        <ProgressIndicator copyArray={stepDescriptions} step={step} />
      )}
      {React.Children.map(children, (child, index) => {
        if (index === step) return child;
        return null;
      })}
    </StepFormContextProvider>
  );
};

export { StepForm, StepFormFooter };
